<template>
    <b-modal
        :id="modalId"
        title="배정 미리보기"
        size="lg"
        ok-only
        ok-title="닫기"
        ok-variant="secondary"
        @show="onShow"
        @hidden="onHidden"
    >
        <p class="text-secondary small">
            가상의 신청자가 아래처럼 답했을 때 어느 평가지로 배정되는지 미리 확인합니다.
            (실제 데이터는 바뀌지 않습니다.)
        </p>

        <p-page-empty-component
            v-if="inputKeys.length === 0"
            text="신청 질문이 없어 미리볼 수 없습니다. 먼저 질문을 만들어 주세요."
        />

        <template v-else>
            <b-form-group
                v-for="q in inputKeys"
                :key="q.id"
                :label="labelOf(q)"
                label-class="fw-500"
            >
                <b-form-select
                    v-if="q.value_type === 'list'"
                    v-model="form[q.key_name]"
                    :options="choiceOptions(q)"
                />
                <b-form-input
                    v-else-if="q.value_type === 'int'"
                    v-model.number="form[q.key_name]"
                    type="number"
                    placeholder="숫자 입력"
                />
                <b-form-input
                    v-else
                    v-model="form[q.key_name]"
                    type="text"
                    placeholder="답변 입력"
                />
            </b-form-group>

            <div class="text-right mb-3">
                <b-button variant="primary" :disabled="loading" @click="runPreview">
                    <b-spinner v-if="loading" small class="mr-1"/>
                    이 답으로 배정 시험
                </b-button>
            </div>

            <b-alert v-if="result" :variant="result.variant" show class="mb-0">
                <i :class="['bi', result.icon, 'mr-1']"></i>{{ result.text }}
            </b-alert>
        </template>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {matchCampaign} from '@shared_utils/api/ruleEngine';
import {getTargetCampaignText} from '@shared/components/admin/pages/Management/common-data';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';

export default {
    name: 'AssignmentPreviewModal',
    components: {
        'p-page-empty-component': PageEmptyComponent,
    },
    props: {
        modalId: {type: String, default: 'assignment-preview-modal'},
        projectId: {type: [Number, String], default: null},
        inputKeys: {type: Array, default: () => []},
        campaigns: {type: Array, default: () => []},
    },
    data() {
        return {
            form: {},
            loading: false,
            result: null,
            // 열기/닫기 사이의 늦은 응답을 폐기하기 위한 요청 순번.
            previewToken: 0,
        };
    },
    methods: {
        onShow() {
            this.previewToken += 1; // 이전 in-flight 결과 무효화
            const seed = {};
            this.inputKeys.forEach((q) => {
                seed[q.key_name] = null;
            });
            this.form = seed;
            this.result = null;
            this.loading = false;
        },
        onHidden() {
            this.previewToken += 1; // 닫는 순간 진행 중 요청 결과도 무효화
            this.loading = false;
        },
        labelOf(q) {
            const lt = q.label_texts || {};
            return lt.ko || q.key_name;
        },
        choiceOptions(q) {
            const opts = [{value: null, text: '선택 안 함'}];
            (q.allowed_values || []).forEach((av) => {
                const lt = av.label_texts || {};
                opts.push({value: av.actual_value, text: lt.ko || av.actual_value});
            });
            return opts;
        },
        async runPreview() {
            // 값이 채워진 질문만 user_inputs 로 보낸다 (빈 값은 제외).
            const userInputs = {};
            Object.keys(this.form).forEach((k) => {
                const v = this.form[k];
                if (v !== null && v !== '') userInputs[k] = v;
            });
            if (Object.keys(userInputs).length === 0) {
                this.result = {variant: 'warning', icon: 'bi-exclamation-circle', text: '답을 하나 이상 입력해 주세요.'};
                return;
            }

            // 이 요청의 순번/프로젝트를 캡처. 완료 시 여전히 유효할 때만 결과를 반영한다.
            const token = ++this.previewToken;
            const pid = this.projectId;
            this.loading = true;
            this.result = null;
            try {
                const res = await matchCampaign({project_id: pid, user_inputs: userInputs});
                if (token !== this.previewToken || pid !== this.projectId) return; // 닫힘/재열림/전환 → 폐기
                const data = res.data || {};
                if (data.matched && data.campaign_id != null) {
                    const name = getTargetCampaignText(data.campaign_id, this.campaigns) || `평가지 #${data.campaign_id}`;
                    this.result = {variant: 'success', icon: 'bi-check-circle-fill', text: `이 답이면 → ${name} 로 배정됩니다.`};
                }
                else {
                    // match-campaign 은 미매칭 시 campaign_id:null 을 준다. 실제 신청도
                    // 활성 규칙이 있으면 기본 평가지로 폴백하지 않으므로, "기본 평가지 배정"으로
                    // 안내하지 않는다 (운영 결과와 반대가 되지 않도록).
                    this.result = {
                        variant: 'warning', icon: 'bi-exclamation-circle',
                        text: '이 답으로는 배정할 규칙이 없습니다. 조건에 맞는 배정 규칙을 추가하거나 답을 바꿔보세요.',
                    };
                }
            }
            catch (e) {
                if (token !== this.previewToken || pid !== this.projectId) return; // 폐기
                // 409 uniqueness_violation 등: 백엔드 메시지를 그대로 보여준다 (성공 위장 금지).
                const msg = getErrorMessage(e, '미리보기 중 오류가 발생했습니다.');
                this.result = {variant: 'danger', icon: 'bi-x-circle', text: msg};
            }
            finally {
                if (token === this.previewToken && pid === this.projectId) this.loading = false;
            }
        },
    },
};
</script>
